import { AxiosResponse } from 'axios'
import { QueryClient, queryOptions } from '@tanstack/react-query'
import { createUrl } from './helpers'
import axiosAuth from './config'
import { NBinder } from '../interfaces/services/binder.interfaces'
import { BinderUpdatedPayload } from '../generated/api/live-events'
import { sortStringsAlphabetically } from '../helpers/sorting'

export const getPerUnit = (
  societyId: string,
  unitId: string,
  folderId?: string
): Promise<AxiosResponse<NBinder.NGetPerUser.IResponse>> => {
  if (folderId) {
    return axiosAuth.get<NBinder.NGetPerUser.IResponse>(
      createUrl(`/binder/getPerUnit/${societyId}/${unitId}/${folderId}`)
    )
  }
  return axiosAuth.get<NBinder.NGetPerUser.IResponse>(
    createUrl(`/binder/getPerUnit/${societyId}/${unitId}`)
  )
}

export const userHasBinders = (
  societyId: string
): Promise<AxiosResponse<NBinder.NGetBySociety.IResponse>> => {
  return axiosAuth.get<NBinder.NGetBySociety.IResponse>(
    createUrl(`/binder/userHasBinders/${societyId}`)
  )
}

export const publishBinder = (
  societyId: string,
  body: NBinder.NPublish.IRequestBody
): Promise<AxiosResponse<NBinder.NPublish.IResponse>> => {
  return axiosAuth.patch<NBinder.NPublish.IResponse>(
    createUrl(`/binder/publish/${societyId}`),
    body
  )
}

export const unpublishBinder = (
  societyId: string,
  body: NBinder.NPublish.IRequestBody
): Promise<AxiosResponse<NBinder.NPublish.IResponse>> => {
  return axiosAuth.patch<NBinder.NPublish.IResponse>(
    createUrl(`/binder/unpublish/${societyId}`),
    body
  )
}

export const patchBinderDocument = (params: {
  id: string
  body: NBinder.NPatch.IRequestBody
}): Promise<AxiosResponse<NBinder.NPatch.IResponse>> => {
  return axiosAuth.patch<NBinder.NPatch.IResponse>(
    createUrl(`/binder/document/${params.id}`),
    params.body
  )
}

export const updateBinderDocumentParent = (params: {
  id: string
  body: NBinder.NPatch.IRequestBody
}): Promise<AxiosResponse<NBinder.NPatch.IResponse>> => {
  return axiosAuth.patch<NBinder.NPatch.IResponse>(
    createUrl(`/binder/document/parent/${params.id}`),
    params.body
  )
}

export const createBinderForUnit = (
  societyId: string,
  unitId: string
): Promise<AxiosResponse<NBinder.NCreateBinder.IResponse>> => {
  return axiosAuth.post<NBinder.NCreateBinder.IResponse>(
    createUrl(`/binder/create/${societyId}/${unitId}`)
  )
}

export const createBinderDocument = (
  societyId: string,
  body: NBinder.NCreate.IRequestBody
): Promise<AxiosResponse<NBinder.NCreate.IResponse>> => {
  return axiosAuth.post<NBinder.NCreate.IResponse>(
    createUrl(`/binder/document/${societyId}`),
    body
  )
}

export const createBinderDocuments = (
  societyId: string,
  unitId: string,
  body: NBinder.NCreateMultiple.IRequestBody
): Promise<AxiosResponse<NBinder.NCreateMultiple.IResponse>> => {
  return axiosAuth.post<NBinder.NCreateMultiple.IResponse>(
    createUrl(`/binder/documents/${societyId}/${unitId}`),
    body
  )
}

export const createBinderThumbnail = (
  societyId: string,
  body: NBinder.NCreateThumbnail.IRequestBody
): Promise<AxiosResponse<NBinder.NCreateThumbnail.IResponse>> => {
  return axiosAuth.post<NBinder.NCreateThumbnail.IResponse>(
    createUrl(`/binder/document/thumbnail/${societyId}`),
    body
  )
}

export const getDocument = (
  societyId: string,
  documentId: string
): Promise<AxiosResponse<NBinder.NGetById.IResponse>> => {
  return axiosAuth.get<NBinder.NGetById.IResponse>(
    createUrl(`/binder/document/${societyId}/${documentId}`)
  )
}

export const getAllSocietyBinders = (
  societyId: string
): Promise<AxiosResponse<NBinder.NGetAllBinders.IResponse>> => {
  return axiosAuth.get<NBinder.NGetAllBinders.IResponse>(
    createUrl(`/binder/all/${societyId}`)
  )
}

export const getAllFileCollisions = (
  filePaths: string[],
  unitIds: string[],
  societyId: string,
  folderId?: string
): Promise<AxiosResponse<NBinder.NGetAllFileCollisions.IResponse>> => {
  return axiosAuth.post<NBinder.NGetAllFileCollisions.IResponse>(
    createUrl(`/binder/fileCollisions/${societyId}`),
    { data: { filePaths, unitIds, folderId } }
  )
}

export const deleteDocument = (
  id: string
): Promise<AxiosResponse<NBinder.NDel.IResponse>> => {
  return axiosAuth.delete<NBinder.NDel.IResponse>(
    createUrl(`/binder/document/${id}`)
  )
}

export const binderQueries = {
  getBinderPerUnit: (societyId: string, unitId: string, folderId?: string) =>
    queryOptions({
      queryKey: [
        { scope: 'binders', entity: 'binder', societyId, unitId, folderId },
      ],
      queryFn: async ({ queryKey }) => {
        const binders = (
          await getPerUnit(
            queryKey[0].societyId!,
            queryKey[0].unitId!,
            queryKey[0].folderId
          )
        ).data.data
        const sortedBinders = {
          binderData: binders.binderData.sort((a, b) => {
            const typeSort = sortStringsAlphabetically(b.type, a.type)
            if (typeSort !== 0) return typeSort
            return sortStringsAlphabetically(a.name, b.name)
          }),
          binderId: binders.binderId,
          publishedAt: binders.publishedAt,
        }
        return sortedBinders
      },
      enabled: !!societyId,
    }),
  userHasBinders: (societyId: string) =>
    queryOptions({
      queryKey: [{ scope: 'binders', entity: 'binder', societyId }],
      queryFn: async ({ queryKey }) => {
        return (await userHasBinders(queryKey[0].societyId!)).data.data
      },
      enabled: !!societyId,
    }),
  getDocument: (societyId?: string, documentId?: string) =>
    queryOptions({
      queryKey: [
        { scope: 'binders', entity: 'document', societyId, documentId },
      ],
      queryFn: async ({ queryKey }) => {
        return (
          await getDocument(queryKey[0].societyId!, queryKey[0].documentId!)
        ).data.data
      },
      enabled: !!documentId,
    }),
  getAllBinders: (societyId: string) =>
    queryOptions({
      queryKey: [{ scope: 'binders', entity: 'allBinders', societyId }],
      queryFn: async ({ queryKey }) => {
        return (await getAllSocietyBinders(queryKey[0].societyId!)).data.data
      },
      enabled: !!societyId,
    }),
  checkFileCollisions: (
    filePaths: string[],
    unitIds: string[],
    societyId: string,
    folderId?: string
  ) =>
    queryOptions({
      queryKey: [
        {
          scope: 'binders',
          entity: 'getAllFileCollisions',
          filePaths,
          unitIds,
          societyId,
          folderId,
        },
      ],
      queryFn: async ({ queryKey }) => {
        return (
          await getAllFileCollisions(
            queryKey[0].filePaths!,
            queryKey[0].unitIds!,
            queryKey[0].societyId!,
            queryKey[0].folderId
          )
        ).data.data
      },
      enabled:
        Array.isArray(unitIds) &&
        unitIds.length > 0 &&
        Array.isArray(filePaths) &&
        filePaths.length > 0 &&
        typeof societyId === 'string' &&
        societyId.length > 0,
    }),
}

export const applyBinderUpdated = async (
  client: QueryClient,
  { societyId, unitId, action }: BinderUpdatedPayload
): Promise<void> => {
  const publishing = action === 'published' || action === 'unpublished'
  // The society's binder list counts files and folders and shows publishing.
  const changesSocietyList =
    action !== 'document-edited' && action !== 'document-moved'
  await Promise.all([
    client.invalidateQueries({
      queryKey: [{ scope: 'binders', entity: 'binder', societyId, unitId }],
    }),
    publishing &&
      client.invalidateQueries({
        queryKey: binderQueries.userHasBinders(societyId).queryKey,
        exact: true,
      }),
    changesSocietyList &&
      client.invalidateQueries({
        queryKey: binderQueries.getAllBinders(societyId).queryKey,
      }),
  ])
}
